import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PlaneTakeoff, CheckCircle2, XCircle, Clock, Info, UserCheck } from "lucide-react";
import SynapseLoader from "@/components/SynapseLoader";

/** Sezione "Assenza / Sostituzione" per MMG.
 *  Consente di:
 *   - chiedere a un altro MMG di sostituirti per un periodo di ferie;
 *   - accettare/rifiutare gli inviti di sostituzione ricevuti;
 *   - annullare una sostituzione attiva.
 */
export default function AbsenceSection({ user }) {
  const isMMG = user?.profession === "medico_medicina_generale";
  const [absence, setAbsence] = useState(null);
  const [invitations, setInvitations] = useState([]);
  const [mmgs, setMmgs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ substitute_user_id: "", from_date: "", until_date: "", note: "" });

  const load = async () => {
    try {
      const [a, inv] = await Promise.all([
        api.get("/absence/me"),
        api.get("/absence/invitations"),
      ]);
      setAbsence(a.data?.absence || null);
      setInvitations(inv.data || []);
    } catch {}
  };
  useEffect(() => {
    load();
    if (isMMG) {
      api.get("/contacts", { params: { role: "medico_medicina_generale" } })
        .then(({ data }) => setMmgs(data || []))
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMMG]);

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const activeInvitation = invitations[0]; // il modello permette una sola sostituzione alla volta per invitante

  const submitRequest = async () => {
    if (!form.substitute_user_id) return toast.error("Scegli un sostituto");
    if (!form.from_date || !form.until_date) return toast.error("Inserisci le date di partenza e rientro");
    setBusy(true);
    try {
      await api.post("/absence/request-substitute", form);
      toast.success("Richiesta di sostituzione inviata");
      setForm({ substitute_user_id: "", from_date: "", until_date: "", note: "" });
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setBusy(false); }
  };

  const cancelRequest = async () => {
    setBusy(true);
    try {
      await api.post("/absence/cancel");
      toast.success("Sostituzione annullata");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setBusy(false); }
  };

  const respondInvitation = async (accept) => {
    setBusy(true);
    try {
      await api.post("/absence/respond", { accept });
      toast.success(accept ? "Sostituzione accettata" : "Sostituzione rifiutata");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    } finally { setBusy(false); }
  };

  const activeAbsence = absence && ["pending", "accepted"].includes(absence.status) ? absence : null;

  return (
    <div data-testid="absence-section">
      {/* Inviti come sostituto (visibile a tutti gli MMG) */}
      {invitations.length > 0 && (
        <div className="mb-4 p-4 rounded-xl border border-amber-200 bg-amber-50" data-testid="absence-invitations">
          <div className="flex items-start gap-3">
            <UserCheck className="h-5 w-5 text-amber-700 mt-0.5" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-amber-900">
                Richiesta di sostituzione da <span className="underline">{activeInvitation.from_user?.name}</span>
              </div>
              <div className="text-sm text-amber-800 mt-1">
                Ti chiede di sostituirlo <strong>dal {activeInvitation.from_date}</strong> al <strong>{activeInvitation.until_date}</strong>.
                {activeInvitation.note && <span className="block italic text-amber-700 mt-1">"{activeInvitation.note}"</span>}
              </div>
              <div className="text-xs text-amber-700 mt-2">
                Durante il periodo, ogni nuova richiesta destinata al collega verrà reindirizzata a te. Le comunicazioni già aperte restano sue.
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={() => respondInvitation(true)} disabled={busy}
                        data-testid="absence-accept"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <CheckCircle2 className="h-4 w-4 mr-1.5" /> Accetta
                </Button>
                <Button size="sm" variant="outline" onClick={() => respondInvitation(false)} disabled={busy}
                        data-testid="absence-reject"
                        className="border-red-200 text-red-700 hover:bg-red-50">
                  <XCircle className="h-4 w-4 mr-1.5" /> Rifiuta
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {!isMMG && (
        <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-600 flex items-start gap-2">
          <Info className="h-4 w-4 text-slate-500 mt-0.5" />
          <span>La sostituzione per ferie è riservata ai <strong>Medici di Medicina Generale</strong>.</span>
        </div>
      )}

      {isMMG && !activeAbsence && (
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Sto per andare in ferie: chiedo a un collega MMG di gestire le nuove richieste al posto mio nel periodo indicato. Le richieste già aperte restano sotto la mia responsabilità.
          </p>
          <div>
            <Label className="text-xs text-slate-600">Sostituto (MMG verificato)</Label>
            <Select value={form.substitute_user_id}
                    onValueChange={(v) => setForm({ ...form, substitute_user_id: v })}>
              <SelectTrigger data-testid="absence-substitute-select">
                <SelectValue placeholder="Scegli un collega…" />
              </SelectTrigger>
              <SelectContent className="max-h-64">
                {mmgs.length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-500">Nessun MMG verificato disponibile.</div>
                )}
                {mmgs.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} {m.locations?.[0]?.city ? `· ${m.locations[0].city}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-600">Dal</Label>
              <Input type="date" min={today} value={form.from_date}
                     onChange={(e) => setForm({ ...form, from_date: e.target.value })}
                     data-testid="absence-from" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Al</Label>
              <Input type="date" min={form.from_date || today} value={form.until_date}
                     onChange={(e) => setForm({ ...form, until_date: e.target.value })}
                     data-testid="absence-until" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-600">Nota per il sostituto (facoltativa)</Label>
            <Textarea rows={2} maxLength={400} value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                      placeholder="Es. Pazienti cronici da monitorare, note operative…"
                      data-testid="absence-note" />
          </div>
          <Button onClick={submitRequest} disabled={busy}
                  data-testid="absence-request-submit"
                  className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">
            {busy ? <SynapseLoader size="sm" inline className="mr-2" /> : <PlaneTakeoff className="h-4 w-4 mr-2" />}
            Invia richiesta di sostituzione
          </Button>
        </div>
      )}

      {isMMG && activeAbsence && (
        <div className={`p-4 rounded-xl border ${
          activeAbsence.status === "pending" ? "border-amber-200 bg-amber-50" :
          activeAbsence.active ? "border-emerald-200 bg-emerald-50" : "border-sky-200 bg-sky-50"
        }`} data-testid="absence-current">
          <div className="flex items-start gap-3">
            {activeAbsence.status === "pending" ? <Clock className="h-5 w-5 text-amber-700 mt-0.5" /> :
             activeAbsence.active ? <PlaneTakeoff className="h-5 w-5 text-emerald-700 mt-0.5" /> :
                                     <PlaneTakeoff className="h-5 w-5 text-sky-700 mt-0.5" />}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900">
                {activeAbsence.status === "pending"
                  ? `In attesa che ${activeAbsence.substitute_name} accetti`
                  : activeAbsence.active
                    ? `Sei in ferie — rientro il ${activeAbsence.until_date}`
                    : `Ferie programmate dal ${activeAbsence.from_date} al ${activeAbsence.until_date}`}
              </div>
              <div className="text-sm text-slate-700 mt-1">
                Sostituto: <strong>{activeAbsence.substitute_name}</strong> · Periodo: {activeAbsence.from_date} → {activeAbsence.until_date}
              </div>
              {activeAbsence.note && (
                <div className="text-xs italic text-slate-500 mt-1">"{activeAbsence.note}"</div>
              )}
              <div className="mt-3">
                <Button size="sm" variant="outline" onClick={cancelRequest} disabled={busy}
                        data-testid="absence-cancel"
                        className="border-red-200 text-red-700 hover:bg-red-50">
                  <XCircle className="h-4 w-4 mr-1.5" /> Annulla sostituzione
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
